/**
 * useBadgeCounts — 사이드바 카운트 배지 (Sprint 7B-7 → 7B-8 강화)
 *
 * Sprint 7B-8 변경
 *  - Supabase Realtime 직접 구독 대신 (CLAUDE.md 의 "프론트에서 Supabase 직접 접근 금지" 규칙 준수)
 *    아래 메커니즘으로 즉각성 확보:
 *      1) 폴링 주기 60s → 20s (포커스 상태)
 *      2) 백그라운드 시 폴링 일시 중단 → 포커스 복귀 시 즉시 refresh
 *      3) BroadcastChannel('hr-actions') 으로 같은 브라우저의 다른 탭/페이지 액션 즉시 반영
 *      4) refresh() 외부 노출 — 액션 직후 부모가 즉시 refresh 가능
 *
 * 2026-10-15: 모든 신호(폴링·창 복귀·탭 알림·SSE)는 탭 공용 예약기(utils/sharedFetcher)를 거친다 —
 *   장치마다 따로 물어 알림 «일괄 처리» 때 사무실 IP 한도를 넘겼다(HR 화면 429)
 *
 * 기본 폴링 간격은 인자로 override 가능 (intervalMs).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api/apiClient';
import { subscribeNotificationStream } from '../../services/notifications';
import { authScope, createSharedFetcher } from '../../utils/sharedFetcher';

export interface BadgeCounts {
  approvals_incoming: number;
  leaves_pending: number;
  notifications_unread: number;
  /** [D-5] 내가 답해야 할 업무 요청(대기 중) */
  task_requests_incoming: number;
  total: number;
}

const DEFAULT: BadgeCounts = {
  approvals_incoming: 0, leaves_pending: 0, notifications_unread: 0, task_requests_incoming: 0, total: 0,
};

const CHANNEL_NAME = 'hr-actions';

// 탭 안의 모든 장치(사이드바·HR 대시보드)가 한 예약기를 쓴다 — 신호가 몰려도 2초에 한 번
export const badgeCountsFetcher = createSharedFetcher<BadgeCounts>(async () => {
  const res = await api.get<{ success: boolean; data: BadgeCounts }>('/hr/badge-counts');
  if (!res?.data) throw new Error('배지 개수 없음');
  return res.data;
}, 2_000, authScope);

export function useBadgeCounts(intervalMs = 20_000) {
  const [counts, setCounts] = useState<BadgeCounts>(() => badgeCountsFetcher.getLatest() ?? DEFAULT);
  const intervalIdRef = useRef<number | null>(null);

  const startPolling = useCallback(() => {
    if (intervalIdRef.current != null) return;
    intervalIdRef.current = window.setInterval(() => badgeCountsFetcher.request(), intervalMs);
  }, [intervalMs]);

  const stopPolling = useCallback(() => {
    if (intervalIdRef.current != null) {
      window.clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = badgeCountsFetcher.subscribe(setCounts);
    badgeCountsFetcher.request();
    startPolling();

    // ── 1) Visibility 훅: 백그라운드 시 폴링 중단, 복귀 시 공용 예약기로 조회
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        badgeCountsFetcher.request();
        startPolling();
      } else {
        stopPolling();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // ── 2) Window focus / online — 사용자 복귀
    const onFocus = () => badgeCountsFetcher.request();
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);

    // ── 3) BroadcastChannel — 같은 브라우저의 다른 탭에서 «처리» 동작(broadcastHrAction)
    let bc: BroadcastChannel | null = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel(CHANNEL_NAME);
        bc.onmessage = (e) => {
          if (e?.data?.type === 'refresh') badgeCountsFetcher.request();
        };
      }
    } catch (e) { /* unsupported */ }

    // ── 4) Sprint 34→: 공유 SSE 구독 (탭당 단일 연결 — 폴링 보강, 연결 한도 소진 방지)
    //   ⛔ 여기서 다른 탭에 다시 알리지 않는다 — 탭마다 같은 신호를 이미 받는다(알리면 탭·훅 수의 제곱으로 불어난다)
    const unsubscribeStream = subscribeNotificationStream(() => badgeCountsFetcher.request());

    return () => {
      unsubscribe();
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
      if (bc) bc.close();
      unsubscribeStream();
    };
  }, [startPolling, stopPolling]);

  return { counts, refresh: badgeCountsFetcher.fetchNow };
}

/**
 * 액션 후 즉시 모든 탭/페이지에 refresh 신호 broadcast.
 * (결재 승인/반려/휴가 신청·취소 등 후 호출)
 */
export function broadcastHrAction(): void {
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const bc = new BroadcastChannel(CHANNEL_NAME);
      bc.postMessage({ type: 'refresh', at: Date.now() });
      bc.close();
    }
  } catch (e) { /* unsupported */ }
}
